/**
 * Mobile keyboard / visual-viewport helpers.
 * iOS often overlays the keyboard without resizing layout; Android may resize.
 * We publish CSS vars and scroll the focused field into the visible area.
 */

const KEYBOARD_OPEN_PX = 90;

/** Visible height right before focusing an input (used to detect keyboard shrink). */
let heightBeforeFocus = null;

export function resetMobileViewportZoom() {
  if (typeof document === "undefined") return;
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const prev = meta.getAttribute("content") || "";
  if (/maximum-scale\s*=\s*1(\.0)?\b/i.test(prev)) {
    window.scrollTo(0, window.scrollY);
    return;
  }
  meta.setAttribute("content", prev + ", maximum-scale=1");
  window.setTimeout(() => {
    meta.setAttribute("content", prev);
  }, 80);
}

function currentVisibleHeight() {
  const vv = window.visualViewport;
  return vv ? vv.height : window.innerHeight;
}

export function syncVisualViewportCss() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { height: 0, offsetTop: 0, keyboardInset: 0, keyboardOpen: false };
  }

  const root = document.documentElement;
  const vv = window.visualViewport;
  const height = currentVisibleHeight();
  const offsetTop = vv ? vv.offsetTop : 0;
  // iOS：键盘覆盖时 innerHeight 不变，vv 变矮
  const insetByInner = Math.max(0, window.innerHeight - height - offsetTop);
  // Android resizes-content / 部分 WebView：用聚焦前高度差
  const insetByFocus =
    heightBeforeFocus != null ? Math.max(0, heightBeforeFocus - height) : 0;
  const keyboardInset = Math.max(insetByInner, insetByFocus);
  const keyboardOpen = keyboardInset >= KEYBOARD_OPEN_PX;

  root.style.setProperty("--vv-height", `${Math.round(height)}px`);
  root.style.setProperty("--vv-offset-top", `${Math.round(offsetTop)}px`);
  root.style.setProperty("--keyboard-inset", `${Math.round(keyboardInset)}px`);
  root.classList.toggle("keyboard-open", keyboardOpen);

  return { height, offsetTop, keyboardInset, keyboardOpen };
}

function findScrollParent(el) {
  let node = el && el.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const oy = style.overflowY;
    if ((oy === "auto" || oy === "scroll" || oy === "overlay") && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * 把焦点输入滚到可视区（键盘上方）附近，避免停在顶部留下大片空白。
 */
export function ensureInputVisible(el) {
  if (!el || typeof el.getBoundingClientRect !== "function") return;
  const vv = window.visualViewport;
  const padding = 12;
  const viewTop = vv ? vv.offsetTop + padding : padding;
  const viewBottom = vv ? vv.offsetTop + vv.height - padding : window.innerHeight - padding;
  const rect = el.getBoundingClientRect();

  // 弹层：只在 modal-body 内滚，避免整层乱跳
  if (el.closest && el.closest(".modal")) {
    const body = el.closest(".modal-body");
    if (body) {
      const br = body.getBoundingClientRect();
      const visibleBottom = Math.min(viewBottom, br.bottom - 8);
      const visibleTop = Math.max(viewTop, br.top + 8);
      if (rect.bottom > visibleBottom) {
        body.scrollTop += rect.bottom - visibleBottom;
      } else if (rect.top < visibleTop) {
        body.scrollTop -= visibleTop - rect.top;
      }
    }
    return;
  }

  if (rect.bottom <= viewBottom && rect.top >= viewTop) return;

  const delta =
    rect.bottom > viewBottom
      ? rect.bottom - viewBottom
      : rect.top < viewTop
        ? rect.top - viewTop
        : 0;
  if (!delta) return;

  const scroller = findScrollParent(el);
  if (scroller) {
    scroller.scrollTop += delta;
    return;
  }
  if (typeof el.scrollIntoView === "function") {
    try {
      el.scrollIntoView({ block: "end", inline: "nearest", behavior: "smooth" });
    } catch (e) {
      el.scrollIntoView(false);
    }
  }
}

function isEditableField(target) {
  if (!target || !target.matches) return false;
  if (target.matches("textarea, [contenteditable='true']")) return true;
  if (!target.matches("input")) return false;
  const type = (target.getAttribute("type") || "text").toLowerCase();
  return /^(text|search|email|password|tel|url|number|date|time|datetime-local)$/.test(type);
}

/**
 * Keep layout in sync with the keyboard and scroll focused fields into view.
 * Returns an unsubscribe function.
 */
export function subscribeMobileKeyboardLayout(listener) {
  if (typeof window === "undefined") return () => {};

  let focusTimer = null;
  let blurTimer = null;
  let wasKeyboardOpen = false;

  const notify = () => {
    const state = syncVisualViewportCss();
    if (typeof listener === "function") listener(state);
    // 键盘刚弹出或可视区变化时，把当前输入重新贴到键盘上方
    if (state.keyboardOpen && isEditableField(document.activeElement)) {
      const el = document.activeElement;
      requestAnimationFrame(() => ensureInputVisible(el));
      if (!wasKeyboardOpen) {
        window.setTimeout(() => ensureInputVisible(el), 120);
      }
    }
    wasKeyboardOpen = state.keyboardOpen;
  };

  const onFocusIn = (event) => {
    if (!isEditableField(event.target)) return;
    if (blurTimer) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
    // 记录聚焦瞬间高度；键盘抬起后高度下降才算 keyboard-open
    if (heightBeforeFocus == null) {
      heightBeforeFocus = currentVisibleHeight();
    }
    document.documentElement.classList.add("input-focused");
    const el = event.target;
    if (focusTimer) clearTimeout(focusTimer);
    focusTimer = window.setTimeout(() => {
      notify();
      ensureInputVisible(el);
      focusTimer = window.setTimeout(() => {
        notify();
        ensureInputVisible(el);
      }, 280);
    }, 80);
  };

  const onFocusOut = () => {
    if (blurTimer) clearTimeout(blurTimer);
    blurTimer = window.setTimeout(() => {
      if (!isEditableField(document.activeElement)) {
        document.documentElement.classList.remove("input-focused");
        heightBeforeFocus = null;
        notify();
        resetMobileViewportZoom();
      }
    }, 120);
  };

  notify();

  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener("resize", notify);
    vv.addEventListener("scroll", notify);
  }
  window.addEventListener("resize", notify);
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);

  return () => {
    if (focusTimer) clearTimeout(focusTimer);
    if (blurTimer) clearTimeout(blurTimer);
    heightBeforeFocus = null;
    if (vv) {
      vv.removeEventListener("resize", notify);
      vv.removeEventListener("scroll", notify);
    }
    window.removeEventListener("resize", notify);
    document.removeEventListener("focusin", onFocusIn);
    document.removeEventListener("focusout", onFocusOut);
    document.documentElement.classList.remove("keyboard-open", "input-focused");
  };
}
